import i18next from 'i18next';

/** Yup `${path}` / label: translate when it is an i18n key, otherwise show as-is (e.g. dotted paths). */
function L(label: string | undefined, path: string | undefined): string {
  const raw = label || path || '';
  if (!raw) {
    return '';
  }
  return String(i18next.t(raw, { defaultValue: raw }));
}

const en = () => (i18next.language || 'de').startsWith('en');

const mixed = {
  default: (p: { label?: string; path?: string }) =>
    en() ? `${L(p.label, p.path)} is invalid` : `${L(p.label, p.path)} ist ungültig`,
  required: (p: { label?: string; path?: string }) =>
    en() ? `${L(p.label, p.path)} is required` : `${L(p.label, p.path)} ist ein Pflichtfeld`,
  oneOf: (p: { label?: string; path?: string; values?: string }) =>
    en()
      ? `${L(p.label, p.path)} must be one of the following values: ${p.values}`
      : `${L(p.label, p.path)} muss einem der folgenden Werte entsprechen: ${p.values}`,
  notOneOf: (p: { label?: string; path?: string; values?: string }) =>
    en()
      ? `${L(p.label, p.path)} must not be one of the following values: ${p.values}`
      : `${L(p.label, p.path)} darf keinem der folgenden Werte entsprechen: ${p.values}`,
};

const string = {
  length: (p: { label?: string; path?: string; length?: number }) =>
    en()
      ? `${L(p.label, p.path)} must be exactly ${p.length} characters`
      : `${L(p.label, p.path)} muss genau ${p.length} Zeichen lang sein`,
  min: (p: { label?: string; path?: string; min?: number }) =>
    en()
      ? `${L(p.label, p.path)} must be at least ${p.min} characters`
      : `${L(p.label, p.path)} muss mindestens ${p.min} Zeichen lang sein`,
  max: (p: { label?: string; path?: string; max?: number }) =>
    en()
      ? `${L(p.label, p.path)} must be at most ${p.max} characters`
      : `${L(p.label, p.path)} darf höchstens ${p.max} Zeichen lang sein`,
  matches: (p: { label?: string; path?: string; regex?: unknown }) =>
    en()
      ? `${L(p.label, p.path)} must match the following: "${p.regex}"`
      : `${L(p.label, p.path)} muss wie folgt aussehen: "${p.regex}"`,
  email: (p: { label?: string; path?: string }) =>
    en()
      ? `${L(p.label, p.path)} must be a valid email address`
      : `${L(p.label, p.path)} muss eine gültige E-Mail-Adresse enthalten`,
  url: (p: { label?: string; path?: string }) =>
    en() ? `${L(p.label, p.path)} must be a valid URL` : `${L(p.label, p.path)} muss eine gültige URL sein`,
  trim: (p: { label?: string; path?: string }) =>
    en()
      ? `${L(p.label, p.path)} must not include leading or trailing spaces`
      : `${L(p.label, p.path)} darf keine Leerzeichen am Anfang oder Ende enthalten`,
  lowercase: (p: { label?: string; path?: string }) =>
    en() ? `${L(p.label, p.path)} must be lowercase` : `${L(p.label, p.path)} darf nur Kleinschreibung enthalten`,
  uppercase: (p: { label?: string; path?: string }) =>
    en() ? `${L(p.label, p.path)} must be uppercase` : `${L(p.label, p.path)} darf nur Großschreibung enthalten`,
};

const number = {
  min: (p: { label?: string; path?: string; min?: number }) =>
    en()
      ? `${L(p.label, p.path)} must be greater than or equal to ${p.min}`
      : `${L(p.label, p.path)} muss größer oder gleich ${p.min} sein`,
  max: (p: { label?: string; path?: string; max?: number }) =>
    en()
      ? `${L(p.label, p.path)} must be less than or equal to ${p.max}`
      : `${L(p.label, p.path)} muss kleiner oder gleich ${p.max} sein`,
  lessThan: (p: { label?: string; path?: string; less?: number }) =>
    en() ? `${L(p.label, p.path)} must be less than ${p.less}` : `${L(p.label, p.path)} muss kleiner sein als ${p.less}`,
  moreThan: (p: { label?: string; path?: string; more?: number }) =>
    en() ? `${L(p.label, p.path)} must be greater than ${p.more}` : `${L(p.label, p.path)} muss größer sein als ${p.more}`,
  notEqual: (p: { label?: string; path?: string; notEqual?: unknown }) =>
    en()
      ? `${L(p.label, p.path)} must not be equal to "${p.notEqual}"`
      : `${L(p.label, p.path)} darf nicht gleich sein mit "${p.notEqual}"`,
  positive: (p: { label?: string; path?: string }) =>
    en() ? `${L(p.label, p.path)} must be a positive number` : `${L(p.label, p.path)} muss eine positive Zahl sein`,
  negative: (p: { label?: string; path?: string }) =>
    en() ? `${L(p.label, p.path)} must be a negative number` : `${L(p.label, p.path)} muss eine negative Zahl sein`,
  integer: (p: { label?: string; path?: string }) =>
    en() ? `${L(p.label, p.path)} must be an integer` : `${L(p.label, p.path)} muss eine ganze Zahl sein`,
};

const date = {
  min: (p: { label?: string; path?: string; min?: unknown }) =>
    en() ? `${L(p.label, p.path)} must be later than ${p.min}` : `${L(p.label, p.path)} muss später sein als ${p.min}`,
  max: (p: { label?: string; path?: string; max?: unknown }) =>
    en() ? `${L(p.label, p.path)} must be earlier than ${p.max}` : `${L(p.label, p.path)} muss früher sein als ${p.max}`,
};

const boolean = {};

const object = {
  noUnknown: (p: { label?: string; path?: string }) =>
    en()
      ? `${L(p.label, p.path)} field contains keys that are not specified in the object shape`
      : `${L(p.label, p.path)}-Feld darf keine Schlüssel verwenden, die nicht im "Objekt-Shape" definiert wurden`,
};

const array = {
  min: (p: { label?: string; path?: string; min?: number }) =>
    en()
      ? `${L(p.label, p.path)} field must have at least ${p.min} items`
      : `${L(p.label, p.path)}-Feld muss mindestens ${p.min} Einträge haben`,
  max: (p: { label?: string; path?: string; max?: number }) =>
    en()
      ? `${L(p.label, p.path)} field must have at most ${p.max} items`
      : `${L(p.label, p.path)}-Feld darf höchstens ${p.max} Einträge haben`,
};

const yupLocaleI18n = {
  mixed,
  string,
  number,
  date,
  object,
  array,
  boolean,
};

export default yupLocaleI18n;
